import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import saasApi from '../../utils/saasApi';

export default function EdgeDevicesPage() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [name, setName] = useState('Edge Windows');
  const [installations, setInstallations] = useState([]);
  const [artifact, setArtifact] = useState(null);
  const [code, setCode] = useState('');
  const [command, setCommand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async (action) => {
    setBusy(true);
    setError('');
    try { await action(); } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }, []);
  const refresh = useCallback(() => run(async () => {
    const result = await saasApi.getEdgeInstallations({ branch_id: branchId });
    setInstallations(result.installations || []);
  }), [branchId, run]);

  useEffect(() => {
    let mounted = true;
    Promise.all([saasApi.getBranches(), saasApi.getEdgeArtifact()])
      .then(([rows, release]) => { if (mounted) { setBranches(rows); setArtifact(release); } })
      .catch((err) => { if (mounted) setError(err.message); });
    return () => { mounted = false; };
  }, []);
  useEffect(() => {
    let mounted = true;
    saasApi.getEdgeInstallations({ branch_id: branchId })
      .then((result) => { if (mounted) setInstallations(result.installations || []); })
      .catch((err) => { if (mounted) setError(err.message); });
    return () => { mounted = false; };
  }, [branchId]);

  return <Stack spacing={3} sx={{ maxWidth: 1100 }}>
    {error && <Alert severity="error">{error}</Alert>}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
      <FormControl size="small" sx={{ minWidth: 220 }}><InputLabel>Sucursal</InputLabel>
        <Select label="Sucursal" value={branchId} onChange={(event) => { setBranchId(event.target.value); setCode(''); }}>
          <MenuItem value="">Todas</MenuItem>
          {branches.map((branch) => <MenuItem key={branch.id} value={String(branch.id)}>{branch.name}</MenuItem>)}
        </Select>
      </FormControl>
      <Button variant="outlined" onClick={refresh} disabled={busy}>Actualizar</Button>
      {busy && <CircularProgress size={20} aria-label="Cargando" />}
    </Stack>
    <Box component="section">
      <Typography variant="h6">Vincular Edge</Typography>
      <Typography variant="body2" color="text.secondary">El codigo vence en 10 minutos y solo puede canjearse una vez.</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
        <TextField size="small" label="Nombre" value={name} onChange={(event) => setName(event.target.value)} inputProps={{ maxLength: 160 }} />
        <Button variant="contained" disabled={!branchId || !name.trim() || busy} onClick={() => run(async () => {
          const result = await saasApi.createEdgePairing({ branch_id: Number(branchId), name });
          setCode(result.code);
        })}>Crear codigo</Button>
      </Stack>
      {code && <Alert severity="info" sx={{ mt: 2 }}>Codigo temporal: <strong>{code}</strong>. Ingresalo en Edge; no lo compartas fuera de la instalacion.</Alert>}
      {artifact?.available ? <Button href={artifact.url} target="_blank" rel="noopener noreferrer" sx={{ mt: 2 }}>Descargar Edge {artifact.version}</Button>
        : <Typography color="text.secondary" sx={{ mt: 2 }}>Descarga no disponible hasta publicar una version oficial verificada.</Typography>}
    </Box>
    <Box component="section">
      <Typography variant="h6" sx={{ mb: 1 }}>Instalaciones</Typography>
      {installations.length === 0 ? <Typography color="text.secondary">No hay instalaciones vinculadas.</Typography> :
        <Stack spacing={1}>{installations.map((edge) => <Box key={edge.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box><Typography fontWeight={600}>{edge.name}</Typography><Typography variant="body2" color="text.secondary">{edge.connection === 'connected' ? 'Heartbeat reciente' : edge.connection === 'revoked' ? 'Revocado' : 'Sin heartbeat reciente'} · {edge.camera_count} camaras registradas</Typography></Box>
            <Stack direction="row" spacing={1}>
              <Button disabled={busy || Boolean(edge.revoked_at)} onClick={() => run(async () => { const result = await saasApi.discoverEdge(edge.id); setCommand({ id: result.command_id, status: 'queued', result_json: null }); })}>Buscar ONVIF</Button>
              <Button color="error" disabled={busy || Boolean(edge.revoked_at)} onClick={() => run(async () => { await saasApi.revokeEdge(edge.id); await refresh(); })}>Revocar</Button>
            </Stack>
          </Stack>
        </Box>)}</Stack>}
    </Box>
    {command && <Box component="section">
      <Typography variant="h6">Resultado de busqueda</Typography>
      <Typography variant="body2" color="text.secondary">Estado: {command.status}</Typography>
      <Button disabled={busy} onClick={() => run(async () => setCommand(await saasApi.getEdgeCommand(command.id)))}>Consultar resultado</Button>
      {(command.result_json || []).map((camera) => <Stack key={camera.local_ref} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ py: 1 }}>
        <Typography>{camera.name} {camera.ip && `(${camera.ip})`}</Typography>
        <Button disabled={busy} onClick={() => run(async () => { await saasApi.registerEdgeCamera(command.id, { local_ref: camera.local_ref, name: camera.name }); await refresh(); })}>Registrar sin activar</Button>
      </Stack>)}
    </Box>}
  </Stack>;
}
